<template>
  <dx-modal
    :value="show"
    @input="close"
    title="Convênios, serviços e profissionais"
    id="schedule-bulk-items-modal"
    size="lg"
  >
    <div class="loading loading-lg" v-if="loadingSummary"/>
    <template v-else>
      <div class="bulk-head">
        <span class="bulk-eyebrow">
          {{ total }} {{ total === 1 ? 'horário selecionado' : 'horários selecionados' }}
        </span>
        <h5 class="bulk-title">
          {{ isAdd ? 'Incluir nos horários selecionados' : 'Remover dos horários selecionados' }}
        </h5>
        <p class="bulk-hint" v-if="isAdd">
          Marque o que deve entrar e confira ao lado em quantos horários vai mudar.
          Quem já tem o item continua como está.
        </p>
        <p class="bulk-hint" v-else>
          A lista mostra só o que existe nos horários selecionados.
          Marque o que deve sair; o restante de cada horário não muda.
        </p>
      </div>

      <div class="mode-cards">
        <div
          class="mode-card"
          :class="{ 'is-active': isAdd }"
          @click="setAction('add')"
        >
          <span class="mode-icon"><fa-icon :icon="['fal', 'plus-circle']"/></span>
          <span class="mode-text">
            <strong>Incluir</strong>
            <small>Entra nos horários que ainda não têm</small>
          </span>
          <span class="mode-check" v-if="isAdd">
            <fa-icon :icon="['fal', 'check-circle']"/>
          </span>
        </div>
        <div
          class="mode-card is-remove"
          :class="{ 'is-active': !isAdd }"
          @click="setAction('remove')"
        >
          <span class="mode-icon"><fa-icon :icon="['fal', 'minus-circle']"/></span>
          <span class="mode-text">
            <strong>Remover</strong>
            <small>Sai dos horários que têm</small>
          </span>
          <span class="mode-check" v-if="!isAdd">
            <fa-icon :icon="['fal', 'check-circle']"/>
          </span>
        </div>
      </div>

      <div class="btn-group bulk-tabs">
        <button
          v-for="item in tabs"
          :key="item.id"
          class="btn btn-sm view-toggle"
          :class="{ 'is-on': tab === item.id }"
          @click="tab = item.id"
        >
          <fa-icon :icon="['fal', item.icon]"/>{{ item.title }}
          <span class="tab-count" v-if="pickedCount(item.id) > 0">{{ pickedCount(item.id) }}</span>
        </button>
      </div>

      <div class="step-layout">
        <div class="step-main">
          <step-picker
            v-if="tab === 'insurance'"
            :key="`insurance-${action}`"
            :items="planOptions"
            :selected-ids="picked.insurance.map(({ id }) => id)"
            :loading="isAdd && loadingPlans"
            :has-any="planOptions.length > 0 || (isAdd && loadingPlans)"
            :creatable="false"
            :add-label="isAdd ? 'Adicionar' : 'Escolher'"
            placeholder="Buscar operadora ou plano..."
            :empty-title="emptyTexts.insurance.title"
            :empty-hint="emptyTexts.insurance.hint"
            none-label="Nenhum convênio encontrado"
            @add="addPicked('insurance', $event)"
          />
          <step-picker
            v-if="tab === 'expense'"
            :key="`expense-${action}`"
            :items="expenseOptions"
            :selected-ids="picked.expense.map(({ id }) => id)"
            :loading="isAdd && loadingExpenses"
            :has-any="isAdd ? hasAnyExpense : expenseOptions.length > 0"
            :remote="isAdd"
            :creatable="false"
            :add-label="isAdd ? 'Adicionar' : 'Escolher'"
            placeholder="Buscar serviço por nome..."
            :empty-title="emptyTexts.expense.title"
            :empty-hint="emptyTexts.expense.hint"
            none-label="Nenhum serviço encontrado"
            @search="searchExpenses"
            @add="addPicked('expense', $event)"
          />
          <step-picker
            v-if="tab === 'professional'"
            :key="`professional-${action}`"
            :items="professionalOptions"
            :selected-ids="picked.professional.map(({ id }) => id)"
            :loading="isAdd && loadingProfessionals"
            :has-any="professionalOptions.length > 0 || (isAdd && loadingProfessionals)"
            :creatable="false"
            :add-label="isAdd ? 'Adicionar' : 'Escolher'"
            placeholder="Buscar profissional por nome, conselho ou especialidade..."
            :empty-title="emptyTexts.professional.title"
            :empty-hint="emptyTexts.professional.hint"
            none-label="Nenhum profissional encontrado"
            @add="addPicked('professional', $event)"
          />
        </div>

        <aside class="step-side" :class="{ 'is-remove': !isAdd }">
          <div class="side-head">
            <span class="side-title">
              <fa-icon :icon="['fal', isAdd ? 'plus-circle' : 'minus-circle']"/>
              {{ isAdd ? 'Vai entrar' : 'Vai sair' }}
            </span>
            <span class="side-count" v-if="pickedTotal > 0">
              {{ pickedTotal }} {{ pickedTotal === 1 ? 'item' : 'itens' }}
            </span>
          </div>
          <div class="side-scroll" v-if="pickedTotal > 0">
            <div class="side-group" v-for="group in pickedGroups" :key="group.id">
              <div class="side-group-title">
                <fa-icon :icon="['fal', group.icon]"/>{{ group.title }}
              </div>
              <ul class="side-list">
                <li
                  class="side-item"
                  :class="{ 'has-limit': isAdd && group.id === 'insurance' }"
                  v-for="(item, i) in picked[group.id]"
                  :key="item.id"
                >
                  <span class="side-text">
                    <span class="side-name" :title="item.title">
                      {{ item.name }}
                      <span class="side-plan" v-if="item.detail">· {{ item.detail }}</span>
                    </span>
                    <span
                      class="side-meta"
                      :class="{ 'text-warning': affected(group.id, item.id) === 0 }"
                    >{{ affectedLabel(group.id, item.id) }}</span>
                  </span>
                  <label
                    class="side-limit tooltip tooltip-left"
                    data-tooltip="Limite por dia"
                    v-if="isAdd && group.id === 'insurance'"
                  >
                    <input
                      type="text"
                      class="form-input input-sm text-center"
                      v-model="item.dailyLimit"
                      v-mask="'000'"
                      placeholder="∞"
                    >
                  </label>
                  <button
                    class="btn btn-sm btn-action btn-icon btn-gray side-remove tooltip tooltip-left"
                    data-tooltip="Tirar da lista"
                    @click="picked[group.id].splice(i, 1)"
                  ><fa-icon :icon="['fal', 'times']"/></button>
                </li>
              </ul>
            </div>
          </div>
          <div class="side-empty" v-else>
            <fa-icon :icon="['fal', isAdd ? 'plus-circle' : 'minus-circle']" size="2x"/>
            <strong>Nada marcado ainda</strong>
            <span>Marque na lista ao lado e clique em {{ isAdd ? 'Adicionar' : 'Escolher' }}.</span>
          </div>
        </aside>
      </div>
    </template>

    <template slot="footer">
      <span class="bulk-impact" v-if="!loadingSummary && pickedTotal > 0">
        <template v-if="changedHours > 0">
          Altera <strong>{{ changedHours }}</strong> de {{ total }}
          {{ total === 1 ? 'horário' : 'horários' }}
        </template>
        <template v-else>Nenhum horário muda com essa seleção</template>
      </span>
      <button
        class="btn btn-icon btn-icon-left mr-1"
        :class="[isAdd ? 'btn-primary' : 'btn-error', { loading: saving }]"
        :disabled="saving || loadingSummary || changedHours === 0"
        @click="save"
      >
        <fa-icon :icon="['fal', isAdd ? 'plus-circle' : 'minus-circle']"/>
        {{ isAdd ? 'Incluir' : 'Remover' }}
      </button>
      <button class="btn" :disabled="saving" @click="close">Sair</button>
    </template>
  </dx-modal>
</template>

<script>
import { resetCache } from '@/helpers/api';
import { getName as expenseTypeName } from '@/data/expense-types';
import StepPicker from '@/app/appointment/components/modals/schedule-hour/StepPicker.vue';

const EXPENSE_AVATARS = { consultation: 'Co', exam: 'Ex' };

function initials(name) {
  return String(name || '')
    .replace(/^Dra?\.\s*/i, '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toUpperCase();
}

function professionalKey(id, specialty) {
  return `${id}:${specialty && specialty.code ? specialty.code : ''}`;
}

function blankPicked() {
  return { insurance: [], expense: [], professional: [] };
}

export default {
  components: {
    StepPicker,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    ids: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      action: 'add',
      tab: 'insurance',
      tabs: [
        { id: 'insurance', title: 'Convênios', icon: 'shield-check' },
        { id: 'expense', title: 'Serviços', icon: 'stethoscope' },
        { id: 'professional', title: 'Profissionais', icon: 'user-md' },
      ],
      loadingSummary: true,
      loadingPlans: false,
      loadingExpenses: false,
      loadingProfessionals: false,
      saving: false,
      summary: {
        total: 0,
        hours: [],
        insurancePlans: [],
        expenses: [],
        professionals: [],
      },
      plans: [],
      expenseResults: [],
      hasAnyExpense: true,
      professionals: [],
      picked: blankPicked(),
    };
  },
  mounted() {
    this.loadSummary();
    this.loadPlans();
    this.searchExpenses({ query: '' });
    this.loadProfessionals();
  },
  computed: {
    isAdd() {
      return this.action === 'add';
    },
    total() {
      return this.summary.total;
    },
    emptyTexts() {
      if (this.isAdd) {
        return {
          insurance: {
            title: 'Nenhum convênio cadastrado ainda',
            hint: 'Cadastre o convênio antes de incluir nos horários.',
          },
          expense: {
            title: 'Nenhum serviço cadastrado ainda',
            hint: 'Cadastre o serviço antes de incluir nos horários.',
          },
          professional: {
            title: 'Nenhum profissional cadastrado ainda',
            hint: 'Cadastre o profissional antes de incluir nos horários.',
          },
        };
      }
      return {
        insurance: {
          title: 'Nenhum convênio nestes horários',
          hint: 'Os horários selecionados não atendem convênios.',
        },
        expense: {
          title: 'Nenhum serviço nestes horários',
          hint: 'Os horários selecionados não têm serviços.',
        },
        professional: {
          title: 'Nenhum profissional nestes horários',
          hint: 'Os horários selecionados não têm profissionais.',
        },
      };
    },
    counts() {
      const toMap = (list, key) => Object.fromEntries(list.map(item => [item[key], item.count]));
      return {
        insurance: toMap(this.summary.insurancePlans, 'id'),
        expense: toMap(this.summary.expenses, 'id'),
        professional: toMap(this.summary.professionals, 'key'),
      };
    },
    planOptions() {
      if (!this.isAdd) {
        return this.summary.insurancePlans.map(item => this.option('insurance', {
          id: item.id,
          name: item.insuranceName,
          detail: item.planName,
          avatar: initials(item.insuranceName),
        }));
      }
      return this.plans.map(item => this.option('insurance', item));
    },
    expenseOptions() {
      const list = this.isAdd ? this.expenseResults : this.summary.expenses;
      return list.map(item => this.option('expense', {
        id: item.id,
        name: item.name,
        typeName: expenseTypeName(item.type),
        avatar: EXPENSE_AVATARS[item.type] || 'Pr',
      }));
    },
    professionalOptions() {
      if (!this.isAdd) {
        return this.summary.professionals.map(item => this.option('professional', {
          id: item.key,
          name: item.name,
          detail: item.specialty && item.specialty.name,
          avatar: initials(item.name),
          source: { id: item.id, specialty: item.specialty },
        }));
      }
      return this.professionals.map(item => this.option('professional', item));
    },
    pickedGroups() {
      return this.tabs.filter(({ id }) => this.picked[id].length > 0);
    },
    pickedTotal() {
      return this.picked.insurance.length
        + this.picked.expense.length
        + this.picked.professional.length;
    },
    changedHours() {
      const plans = this.picked.insurance.map(({ id }) => id);
      const expenses = this.picked.expense.map(({ id }) => id);
      const professionals = this.picked.professional.map(({ id }) => id);
      return this.summary.hours
        .filter(hour => this.hourChanges(hour, plans, expenses, professionals))
        .length;
    },
  },
  methods: {
    option(kind, item) {
      const title = item.detail ? `${item.name} · ${item.detail}` : item.name;
      return {
        id: item.id,
        name: item.name,
        detail: item.detail || '',
        title,
        meta: [item.typeName, this.presenceLabel(kind, item.id)].filter(Boolean).join(' · '),
        warning: this.isAdd && this.affected(kind, item.id) === 0,
        avatar: item.avatar,
        square: kind !== 'professional',
        search: `${title} ${item.search || ''}`,
        source: item.source || null,
      };
    },
    count(kind, id) {
      if (kind === 'professional' && this.isAdd && id.endsWith(':')) {
        return this.summary.hours
          .filter(hour => hour.professionals.some(key => key.startsWith(id)))
          .length;
      }
      return this.counts[kind][id] || 0;
    },
    affected(kind, id) {
      const count = this.count(kind, id);
      return this.isAdd ? this.total - count : count;
    },
    presenceLabel(kind, id) {
      const count = this.count(kind, id);
      if (count === 0) return 'Em nenhum horário';
      if (count === this.total) return 'Em todos os horários';
      return `Em ${count} de ${this.total}`;
    },
    affectedLabel(kind, id) {
      const affected = this.affected(kind, id);
      if (this.isAdd) {
        if (affected === 0) return 'Já está em todos os horários';
        return affected === this.total
          ? 'Entra em todos os horários'
          : `Entra em ${affected} de ${this.total} · os demais já têm`;
      }
      return affected === this.total
        ? 'Sai de todos os horários'
        : `Sai de ${affected} de ${this.total}`;
    },
    hourChanges(hour, plans, expenses, professionals) {
      if (this.isAdd) {
        return plans.some(id => !hour.insurancePlans.includes(id))
          || expenses.some(id => !hour.expenses.includes(id))
          || professionals.some(key => (
            key.endsWith(':')
              ? !hour.professionals.some(item => item.startsWith(key))
              : !hour.professionals.includes(key)
          ));
      }
      return plans.some(id => hour.insurancePlans.includes(id))
        || expenses.some(id => hour.expenses.includes(id))
        || professionals.some(key => hour.professionals.includes(key));
    },
    pickedCount(kind) {
      return this.picked[kind].length;
    },
    setAction(action) {
      if (this.action === action) return;
      this.action = action;
      this.picked = blankPicked();
    },
    addPicked(kind, ids) {
      const options = {
        insurance: this.planOptions,
        expense: this.expenseOptions,
        professional: this.professionalOptions,
      }[kind];
      ids.forEach((id) => {
        const option = options.find(item => item.id === id);
        if (!option || this.picked[kind].some(item => item.id === id)) return;
        this.picked[kind].push({
          id,
          name: option.name,
          detail: option.detail,
          title: option.title,
          source: option.source,
          dailyLimit: '',
        });
      });
    },
    async loadSummary() {
      this.loadingSummary = true;
      await this.$httpX
        .post('/schedule-config/bulk-items/summary', { ids: this.ids })
        .then(({ data }) => {
          this.summary = data;
        })
        .catch((e) => {
          this.$toast.error(e);
          this.close();
        })
        .finally(() => {
          this.loadingSummary = false;
        });
    },
    async loadPlans() {
      this.loadingPlans = true;
      await this.$httpX
        .get('/insurance-plans', { params: { active: true, limit: 0 } })
        .then(({ data }) => {
          this.plans = data.items.map(item => ({
            id: item.plan.id,
            name: item.name,
            detail: item.plan.name,
            avatar: item.plan.type === 'particular' ? 'P' : initials(item.name),
          }));
        })
        .catch(() => {})
        .finally(() => {
          this.loadingPlans = false;
        });
    },
    async searchExpenses({ query }) {
      this.loadingExpenses = true;
      const params = { active: true, limit: 100 };
      if (query) params.search = query;

      await this.$httpX
        .get('/expenses', { params })
        .then(({ data }) => {
          this.expenseResults = data.items.map(item => ({
            id: item.id,
            name: item.name,
            type: item.type,
          }));
          if (!query) {
            this.hasAnyExpense = data.items.length > 0;
          }
        })
        .catch(() => {})
        .finally(() => {
          this.loadingExpenses = false;
        });
    },
    async loadProfessionals() {
      this.loadingProfessionals = true;
      await this.$httpX
        .get('/professionals', { params: { active: true, limit: 0 } })
        .then(({ data }) => {
          this.professionals = data.items.flatMap((item) => {
            const councils = item.professional.councils || [];
            const council = councils.length > 0 ? councils[0] : null;
            const list = item.professional.specialties || [];
            const specialties = list.length > 0 ? list : [{}];
            return specialties.map(specialty => ({
              id: professionalKey(item.id, specialty),
              name: item.name,
              detail: specialty.name,
              avatar: initials(item.name),
              search: council ? council.record : '',
              source: {
                id: item.id,
                specialty: specialty.code ? { code: specialty.code, name: specialty.name } : {},
              },
            }));
          });
        })
        .catch(() => {})
        .finally(() => {
          this.loadingProfessionals = false;
        });
    },
    save() {
      if (this.isAdd) {
        this.submit();
        return;
      }
      const count = this.pickedTotal;
      this.$dialog.show('', {
        html:
          '<div class="text-center">'
          + '<h5 class="text-center">Atenção!</h5>'
          + `<div>Deseja realmente remover ${count} ${count === 1 ? 'item' : 'itens'} `
          + `de ${this.changedHours} ${this.changedHours === 1 ? 'horário' : 'horários'}?</div>`
          + '</div>',
        buttons: [
          { label: 'Não', classes: '' },
          {
            label: 'Sim',
            classes: 'btn-primary btn-error ml-2',
            click: (close) => {
              close();
              this.submit();
            },
          },
        ],
      });
    },
    async submit() {
      this.saving = true;

      const data = {
        action: this.action,
        ids: this.ids,
        insurancePlans: this.picked.insurance.map(item => ({
          id: item.id,
          dailyLimit: item.dailyLimit ? Number(item.dailyLimit) : null,
        })),
        expenses: this.picked.expense.map(({ id }) => id),
        professionals: this.picked.professional.map(({ source }) => ({
          id: source.id,
          specialty: source.specialty && source.specialty.code
            ? { code: source.specialty.code, name: source.specialty.name }
            : null,
        })),
      };

      await this.$httpX
        .post('/schedule-config/bulk-items', data)
        .then(({ data: result }) => {
          const hours = `${result.changed} ${result.changed === 1 ? 'horário' : 'horários'}`;
          this.$toast.show(this.isAdd ? `Incluído em ${hours}` : `Removido de ${hours}`);
          resetCache();
          this.$emit('saved', result);
          this.close();
        })
        .catch((e) => {
          this.$toast.error(e);
        })
        .finally(() => {
          this.saving = false;
        });
    },
    close() {
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss">
@import '@/assets/scss/_variables.scss';

#schedule-bulk-items-modal {
  .bulk-head {
    margin-bottom: $layout-spacing-lg;
  }
  .bulk-eyebrow {
    color: $primary-color;
    display: block;
    font-size: .5rem;
    font-weight: 600;
    letter-spacing: .06em;
    text-transform: uppercase;
  }
  .bulk-title {
    color: $gray-color-900;
    font-size: .9rem;
    font-weight: 700;
    margin: $layout-spacing-sm 0 0;
  }
  .bulk-hint {
    color: $gray-color-dark;
    font-size: $font-size-sm;
    margin: $layout-spacing-sm 0 0;
    max-width: 44rem;
  }

  .mode-cards {
    display: flex;
    gap: $layout-spacing;
    margin-bottom: $layout-spacing-lg;
  }
  .mode-card {
    align-items: center;
    background-color: $gray-color-ultra-light;
    border: $border-width solid $border-color-dark;
    border-radius: $border-radius;
    cursor: pointer;
    display: flex;
    flex: 1;
    gap: $layout-spacing;
    max-width: 16rem;
    padding: $layout-spacing $layout-spacing-lg;
    transition: border-color .15s ease, background-color .15s ease;
    &:hover {
      border-color: $primary-color;
    }
    .mode-icon {
      align-items: center;
      background-color: lighten($primary-color, 33%);
      border-radius: 50%;
      color: $primary-color;
      display: flex;
      flex-shrink: 0;
      height: 1.6rem;
      justify-content: center;
      width: 1.6rem;
    }
    .mode-text {
      display: flex;
      flex-direction: column;
      min-width: 0;
      strong {
        color: $gray-color-900;
      }
      small {
        color: $gray-color-dark;
        font-size: $font-size-xs;
      }
    }
    .mode-check {
      color: $primary-color;
      margin-left: auto;
    }
    &.is-active {
      background-color: lighten($primary-color, 36%);
      border-color: $primary-color;
      .mode-icon {
        background-color: $primary-color;
        color: $light-color;
      }
    }
    &.is-remove {
      &:hover {
        border-color: $error-color;
      }
      .mode-icon {
        background-color: #FDECEC;
        color: $error-color;
      }
      .mode-check {
        color: $error-color;
      }
      &.is-active {
        background-color: #FFF5F5;
        border-color: $error-color;
        .mode-icon {
          background-color: $error-color;
          color: $light-color;
        }
      }
    }
  }

  .bulk-tabs {
    margin-bottom: $layout-spacing;
    .view-toggle {
      align-items: center;
      background-color: $light-color;
      border-color: $border-color-dark;
      color: $gray-color-dark;
      display: inline-flex;
      font-size: $font-size-sm;
      gap: .3rem;
      height: 1.4rem;
      line-height: 1.2rem;
      padding: .05rem .5rem;
      svg {
        color: $secondary-color;
      }
      &:hover {
        color: $secondary-color-dark;
      }
      &.is-on {
        background-color: $gray-color-ultra-light;
        color: $secondary-color-dark;
        font-weight: 600;
      }
      &:focus {
        box-shadow: none;
      }
    }
    .tab-count {
      background-color: $light-color;
      border: 1px solid lighten($primary-color, 26%);
      border-radius: 1rem;
      color: $primary-color;
      font-size: .5rem;
      font-weight: 700;
      line-height: 1;
      min-width: .9rem;
      padding: .14rem .3rem;
      text-align: center;
    }
  }

  .step-layout {
    align-items: start;
    display: grid;
    gap: $layout-spacing-lg;
    grid-template-columns: minmax(0, 11fr) minmax(0, 9fr);
  }

  .step-side {
    background-color: #F2FAF5;
    border: $border-width solid #BFE3CD;
    border-left: 4px solid #2FA36B;
    border-radius: $border-radius;
    display: flex;
    flex-direction: column;
    max-height: 22rem;
    min-height: 12rem;
    .side-title {
      color: #1F7A4E;
      svg {
        color: #2FA36B;
      }
    }
    .side-head {
      border-bottom-color: #BFE3CD;
    }
    .side-count {
      border-color: #BFE3CD;
      color: #1F7A4E;
    }
    .side-group-title {
      color: #1F7A4E;
    }
    .side-item {
      border-bottom-color: #E3F4EA;
    }
    .side-empty {
      svg {
        color: #2FA36B;
      }
      strong {
        color: #1F7A4E;
      }
    }
    &.is-remove {
      background-color: #FFF5F5;
      border-color: #F5C6C6;
      border-left-color: $error-color;
      .side-title {
        color: darken($error-color, 15%);
        svg {
          color: $error-color;
        }
      }
      .side-head {
        border-bottom-color: #F5C6C6;
      }
      .side-count {
        border-color: #F5C6C6;
        color: darken($error-color, 15%);
      }
      .side-group-title {
        color: darken($error-color, 15%);
      }
      .side-item {
        border-bottom-color: #FDECEC;
      }
      .side-empty {
        svg {
          color: $error-color;
        }
        strong {
          color: darken($error-color, 15%);
        }
      }
    }
  }
  .side-head {
    align-items: center;
    border-bottom: $border-width solid;
    display: flex;
    gap: $layout-spacing;
    justify-content: space-between;
    padding: $layout-spacing $layout-spacing-lg;
  }
  .side-title {
    align-items: center;
    display: inline-flex;
    font-size: $font-size-sm;
    font-weight: 700;
    gap: $layout-spacing-sm;
    letter-spacing: .02em;
    text-transform: uppercase;
  }
  .side-count {
    background-color: $light-color;
    border: $border-width solid;
    border-radius: 1rem;
    font-size: $font-size-xs;
    font-weight: 600;
    line-height: 1;
    padding: .18rem .5rem;
    white-space: nowrap;
  }
  .side-scroll {
    overflow: auto;
  }
  .side-group-title {
    align-items: center;
    display: flex;
    font-size: .5rem;
    font-weight: 700;
    gap: .3rem;
    letter-spacing: .06em;
    padding: $layout-spacing-sm $layout-spacing-lg;
    text-transform: uppercase;
  }
  .side-list {
    list-style: none;
    margin: 0;
    padding: 0;
  }
  .side-item {
    align-items: center;
    background-color: $light-color;
    border-bottom: $border-width solid;
    display: grid;
    gap: $layout-spacing;
    grid-template-columns: minmax(0, 1fr) auto;
    margin: 0;
    padding: $layout-spacing-sm $layout-spacing-lg;
    &:last-child {
      border-bottom: 0;
    }
    &.has-limit {
      grid-template-columns: minmax(0, 1fr) auto auto;
    }
    .side-remove {
      opacity: .6;
    }
    &:hover {
      .side-remove {
        opacity: 1;
      }
      .side-name,
      .side-meta {
        overflow: visible;
        text-overflow: clip;
        white-space: normal;
      }
    }
  }
  .side-text {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }
  .side-name {
    color: $gray-color-900;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .side-plan {
    color: $secondary-color;
    font-weight: 500;
  }
  .side-meta {
    color: $gray-color-dark;
    font-size: $font-size-xs;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .side-limit {
    margin: 0;
    .form-input {
      width: 3rem;
    }
  }
  .side-empty {
    align-items: center;
    color: $gray-color-dark;
    display: flex;
    flex: 1;
    flex-direction: column;
    font-size: $font-size-xs;
    gap: $layout-spacing-sm;
    justify-content: center;
    padding: $layout-spacing-xl $layout-spacing-lg;
    text-align: center;
    svg {
      margin-bottom: $layout-spacing-sm;
    }
    strong {
      font-size: $font-size-sm;
    }
  }

  .bulk-impact {
    color: $gray-color-dark;
    float: left;
    font-size: $font-size-xs;
    line-height: 1.8rem;
    strong {
      color: $gray-color-900;
      font-variant-numeric: tabular-nums;
    }
  }

  @media screen and (max-width: $size-md) {
    .step-layout {
      grid-template-columns: 1fr;
    }
    .mode-card {
      max-width: none;
    }
  }
}
</style>
